import { useSnackbarContext } from '@/context/Snackbar/SnackbarContext';
import cacheKeys from '@/data/api/cacheKeys';
import { updatePrice } from '@/data/api/request/productCatalogRequests';
import { formatedAmountToNumber } from '@/utils/amounts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useRef, useState } from 'react';

const validInput = /^$|^\d+(\.\d{0,2})?$/;

export function useEditPriceViewController(props: {
  productId: string;
  skuId: string;
  price: string;
}) {
  const anchorEl = useRef<HTMLDivElement>(null);
  const { showSnackSuccess, showSnackError } = useSnackbarContext();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const [priceText, setPriceText] = useState(props.price);

  const onPriceTextChanged = (event: ChangeEvent<HTMLInputElement>) =>
    validInput.test(event.target.value) && setPriceText(event.target.value);

  const isInvalidPrice = formatedAmountToNumber(priceText) === undefined;

  const savePrice = useMutation({
    mutationFn: async (args: {
      productId: string;
      skuId: string;
      price: number;
    }) => {
      await updatePrice({
        product_id: args.productId,
        sku_id: args.skuId,
        price: args.price,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [cacheKeys.product.catalog] });
      showSnackSuccess('Precio actualizado');
    },
    onError: () => showSnackError('Error al actualizar el precio'),
  });

  const onClickedEditPrice = () => {
    setOpen(true);
    setPriceText(`${formatedAmountToNumber(props.price) ?? 0}`);
  };

  const onClickedSavePrice = () => {
    setOpen(false);
    savePrice.mutate({
      productId: props.productId,
      skuId: props.skuId,
      price: Number(priceText),
    });
  };

  const onClickedCancelEdit = () => setOpen(false);

  return {
    uiState: {
      open: open,
      price: props.price,
      priceText: priceText,
      isInvalidPrice: isInvalidPrice,
    },
    actions: {
      onClickedEditPrice,
      onPriceTextChanged,
      onClickedSavePrice,
      onClickedCancelEdit,
    },
    references: { anchorEl },
  };
}
